import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { API } from '../../utils/config';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  ClipboardCheck, 
  Search, 
  Check, 
  Clock,
  AlertCircle,
  ChevronRight,
  Home,
  FileCheck
} from 'lucide-react';


const SchedeQualitaList = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');
  
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/quality-sheets/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setProperties(data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching quality sheets:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const filteredProperties = properties.filter(p => 
    p.property_name.toLowerCase().includes(search.toLowerCase()) ||
    p.city?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (prop) => {
    if (prop.my_sheet_complete) {
      return (
        <Badge className="bg-green-100 text-green-800">
          <Check className="h-3 w-3 mr-1" />
          Completata
        </Badge>
      );
    }
    if (prop.has_my_sheet) {
      return (
        <Badge className="bg-amber-100 text-amber-800">
          <Clock className="h-3 w-3 mr-1" />
          In bozza
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-600">
        <AlertCircle className="h-3 w-3 mr-1" />
        Da compilare
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            Schede Qualità
          </h1>
          <p className="text-sm text-slate-500">
            Compila la scheda qualità per ogni immobile che gestisci
          </p>
        </div>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {properties.filter(p => p.my_sheet_complete).length}
            </div>
            <div className="text-xs text-green-700">Completate</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">
              {properties.filter(p => p.has_my_sheet && !p.my_sheet_complete).length}
            </div>
            <div className="text-xs text-amber-700">In bozza</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-slate-600">
              {properties.filter(p => !p.has_my_sheet).length}
            </div>
            <div className="text-xs text-slate-600">Da compilare</div>
          </CardContent>
        </Card>
      </div>

      {/* Ricerca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Cerca immobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Lista immobili */}
      <div className="space-y-3">
        {filteredProperties.length === 0 ? (
          <Card className="p-8 text-center text-slate-500">
            <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>Nessun immobile trovato</p>
          </Card>
        ) : (
          filteredProperties.map(prop => (
            <Card 
              key={prop.property_id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/responsabile/scheda-qualita/${prop.property_id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Home className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium">{prop.property_name}</h3>
                      <p className="text-sm text-slate-500">{prop.city}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {prop.my_quality_score !== null && (
                      <div className="text-right mr-2">
                        <div className="text-lg font-bold text-blue-600">{prop.my_quality_score}%</div>
                        <div className="text-xs text-slate-500">Punteggio</div>
                      </div>
                    )}
                    {getStatusBadge(prop)}
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SchedeQualitaList;
